import { createSlice } from "@reduxjs/toolkit";
import {
  getLeads, getTelecallers, getAdminDashboard, getTelecallerDashboard,
  getFollowups, getCallLogs, getLeadTimeline, deleteLead, createLead, updateLead,
  createTelecaller, updateTelecaller, deleteTelecaller, completeFollowup, logCall,
  getStatuses, createStatus, updateStatus, deleteStatus,
  getTags, createTag, updateTag, deleteTag, setLeadTags,
} from "./ThunkActions";
import { ICRMState, REDUCER_NAME } from "./Types";

const initialState: ICRMState = {
  leads: [], total: 0, isLoading: false,
  telecallers: [], telecallersLoading: false,
  statuses: [], tags: [],
  adminDashboard: null, telecallerDashboard: null,
  followups: [], callLogs: [],
  timeline: [], timelineLoading: false,
  errorMessage: null,
};

const crmSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: { resetCRMState: () => initialState },
  extraReducers: (builder) => {
    builder
      .addCase(getLeads.pending, (state) => { state.isLoading = true; state.errorMessage = null; })
      .addCase(getLeads.fulfilled, (state, action) => {
        const d = (action.payload as any)?.data?.data;
        state.leads = d?.leads || [];
        state.total = d?.total || 0;
        state.isLoading = false;
      })
      .addCase(getLeads.rejected, (state) => { state.isLoading = false; state.errorMessage = "Failed to fetch leads"; })

      .addCase(getTelecallers.pending, (state) => { state.telecallersLoading = true; })
      .addCase(getTelecallers.fulfilled, (state, action) => {
        const d = (action.payload as any)?.data?.data;
        state.telecallers = d?.telecallers || [];
        state.telecallersLoading = false;
      })
      .addCase(getTelecallers.rejected, (state) => { state.telecallersLoading = false; })

      .addCase(getAdminDashboard.fulfilled, (state, action) => {
        state.adminDashboard = (action.payload as any)?.data?.data || null;
      })
      .addCase(getTelecallerDashboard.fulfilled, (state, action) => {
        state.telecallerDashboard = (action.payload as any)?.data?.data || null;
      })

      .addCase(getFollowups.fulfilled, (state, action) => {
        state.followups = (action.payload as any)?.data?.data?.followups || [];
      })
      .addCase(getCallLogs.fulfilled, (state, action) => {
        state.callLogs = (action.payload as any)?.data?.data?.callLogs || [];
      })

      .addCase(getLeadTimeline.pending, (state) => { state.timelineLoading = true; })
      .addCase(getLeadTimeline.fulfilled, (state, action) => {
        state.timeline = (action.payload as any)?.data?.data?.timeline || [];
        state.timelineLoading = false;
      })
      .addCase(getLeadTimeline.rejected, (state) => { state.timelineLoading = false; })

      .addCase(createLead.fulfilled, (state, action) => {
        const lead = (action.payload as any)?.data?.data?.lead;
        if (lead) { state.leads = [lead, ...state.leads]; state.total++; }
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        const lead = (action.payload as any)?.data?.data?.lead;
        if (lead) { const i = state.leads.findIndex(l => l.id === lead.id); if (i >= 0) state.leads[i] = { ...state.leads[i], ...lead }; }
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        const id = (action.payload as any)?.data?.data?.id || (action.payload as any)?.data?.id;
        if (id) { state.leads = state.leads.filter(l => l.id !== id); state.total = Math.max(0, state.total - 1); }
      })

      .addCase(createTelecaller.fulfilled, (state, action) => {
        const tc = (action.payload as any)?.data?.data?.telecaller;
        if (tc) state.telecallers = [...state.telecallers, tc];
      })
      .addCase(updateTelecaller.fulfilled, (state, action) => {
        const tc = (action.payload as any)?.data?.data?.telecaller;
        if (tc) { const i = state.telecallers.findIndex(t => t.id === tc.id); if (i >= 0) state.telecallers[i] = tc; }
      })
      .addCase(deleteTelecaller.fulfilled, (state, action) => {
        const id = (action.payload as any)?.data?.data?.id;
        if (id) state.telecallers = state.telecallers.filter(t => t.id !== id);
      })

      .addCase(completeFollowup.fulfilled, (state, action) => {
        const id = (action.payload as any)?.data?.data?.id;
        if (id) state.followups = state.followups.filter(f => f.id !== id);
      })

      .addCase(logCall.fulfilled, (state, action) => {
        const callLog = (action.payload as any)?.data?.data?.callLog;
        if (callLog) state.callLogs = [callLog, ...state.callLogs];
      })

      .addCase(getStatuses.fulfilled, (state, action) => {
        state.statuses = (action.payload as any)?.data?.data?.statuses || [];
      })
      .addCase(createStatus.fulfilled, (state, action) => {
        const status = (action.payload as any)?.data?.data?.status;
        if (status) state.statuses = [...state.statuses, status];
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        const status = (action.payload as any)?.data?.data?.status;
        if (status) { const i = state.statuses.findIndex(s => s.id === status.id); if (i >= 0) state.statuses[i] = status; }
      })
      .addCase(deleteStatus.fulfilled, (state, action) => {
        const id = (action.payload as any)?.data?.data?.id;
        if (id) state.statuses = state.statuses.filter(s => s.id !== id);
      })

      .addCase(getTags.fulfilled, (state, action) => {
        state.tags = (action.payload as any)?.data?.data?.tags || [];
      })
      .addCase(createTag.fulfilled, (state, action) => {
        const tag = (action.payload as any)?.data?.data?.tag;
        if (tag) state.tags = [...state.tags, tag];
      })
      .addCase(updateTag.fulfilled, (state, action) => {
        const tag = (action.payload as any)?.data?.data?.tag;
        if (tag) { const i = state.tags.findIndex(t => t.id === tag.id); if (i >= 0) state.tags[i] = tag; }
      })
      .addCase(deleteTag.fulfilled, (state, action) => {
        const id = (action.payload as any)?.data?.data?.id;
        if (id) state.tags = state.tags.filter(t => t.id !== id);
      })

      .addCase(setLeadTags.fulfilled, (state, action) => {
        const d = (action.payload as any)?.data?.data;
        if (d?.leadId) {
          const i = state.leads.findIndex(l => l.id === d.leadId);
          if (i >= 0) state.leads[i] = { ...state.leads[i], tags: d.tags || [] };
        }
      });
  },
});

export const { resetCRMState } = crmSlice.actions;
export default crmSlice.reducer;
